import { useState, useCallback, useRef, useEffect } from 'react';
import { MarkdownEditor } from '../../ui/markdown-editor';
import { MarkdownInlineDiff } from '../../ui/markdown-inline-diff';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { useVfsContent } from '@/hooks/useVfsContent';
import { useCanvases } from '@/contexts/CanvasContext';
import { rewriteVfsUrisInString } from '@/lib/vfs-url';
import { cn } from '@/lib/cn';
import { useAuiFillPath } from '../AuiFillPathContext';
import type { AuiRendererProps } from '../registry';

const BARE_CONTENT_URI_RE = /^\s*(?:vfs|kb):\/\/\S+\s*$/;
const API_BASE = import.meta.env.VITE_API_URL || '';

export function AuiMarkdownEditor({ node, onAction }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const { conversationId } = useCanvases();

  const rawContent = (props.content as string) ?? '';
  const explicitContentUrl = props.contentUrl as string | undefined;

  // Shorthand: when `content` is exactly one bare `vfs://...` or `kb://...`
  // URI, treat it as a content URL and fetch it. Embedded URIs inside
  // markdown are NOT detected — they render literally.
  const contentIsBareUri = BARE_CONTENT_URI_RE.test(rawContent);
  const derivedContentUrl = contentIsBareUri
    ? rewriteVfsUrisInString(rawContent.trim(), conversationId ?? '', API_BASE)
    : undefined;

  const contentUrl = explicitContentUrl ?? derivedContentUrl;
  const inlineContent = contentIsBareUri ? '' : rawContent;
  const fetched = useVfsContent(contentUrl);
  const content = contentUrl ? (fetched.data ?? '') : inlineContent;

  const previousContent = props.previousContent as string | undefined;
  const editable = (props.editable as boolean) ?? false;
  const title = props.title as string | undefined;
  const minHeight = (props.minHeight as string) ?? '300px';
  // No default cap: when the agent doesn't specify maxHeight, the editor
  // flex-fills its column (canvas card chain wires this up). An explicit
  // maxHeight switches to bounded mode where the editor scrolls internally.
  const maxHeight = props.maxHeight as string | undefined;
  // Only activate flex fill when AuiSurface confirmed this node is on the
  // fill path. Otherwise (e.g. inside the AUI showcase or any non-surface
  // mount) the chain isn't wired and the editor falls back to content height
  // with `minHeight` as a floor.
  const isOnFillPath = useAuiFillPath(node.id);
  const fill = isOnFillPath && maxHeight === undefined;

  const actions = node.component.actions;

  // Local edit state (only used when editable)
  const [editedContent, setEditedContent] = useState(content);
  const [isSaving, setIsSaving] = useState(false);
  const editedRef = useRef(editedContent);

  // Reset local state when agent pushes new content
  useEffect(() => {
    setEditedContent(content);
    editedRef.current = content;
  }, [content]);

  const handleChange = useCallback((value: string) => {
    setEditedContent(value);
    editedRef.current = value;
  }, []);

  const handleSave = useCallback(async () => {
    if (actions?.[0] && onAction) {
      setIsSaving(true);
      try {
        await onAction({
          ...actions[0],
          payload: { ...actions[0].payload, content: editedRef.current },
        });
      } finally {
        setIsSaving(false);
      }
    }
  }, [actions, onAction]);

  // When fill is on, become a flex column so the editor child can claim the
  // remaining height granted by the surface chain (Card → CardContent → Column).
  const wrapperClass = fill ? 'flex flex-col flex-1 min-h-0' : undefined;

  if (contentUrl && fetched.loading) {
    return (
      <div data-testid="aui-markdown-editor" className={wrapperClass}>
        {title && (
          <h4 className="text-sm font-semibold text-foreground mb-2">{title}</h4>
        )}
        <Skeleton
          // Same fill-mode rationale as the editor element below: the inline
          // minHeight would override flex distribution and overflow the wrapper.
          style={fill ? undefined : { minHeight }}
          className={cn('w-full', fill && 'flex-1 min-h-0')}
        />
      </div>
    );
  }

  if (contentUrl && fetched.error) {
    return (
      <div data-testid="aui-markdown-editor" className={wrapperClass}>
        {title && (
          <h4 className="text-sm font-semibold text-foreground mb-2">{title}</h4>
        )}
        <p className="text-sm text-destructive">
          Failed to load content: {fetched.error.message}
        </p>
      </div>
    );
  }

  // When the agent ships an update against an existing content, swap in a
  // non-interactive inline diff. MDXEditor stays the editor; this component
  // owns the review experience. Returning to edit mode happens when the
  // surface drops `previousContent` (or the diff resolves to identical).
  if (previousContent !== undefined && previousContent !== content) {
    return (
      <div data-testid="aui-markdown-editor" className={wrapperClass}>
        {title && (
          <h4 className="text-sm font-semibold text-foreground mb-2">{title}</h4>
        )}
        <MarkdownInlineDiff oldContent={previousContent} newContent={content} />
      </div>
    );
  }

  // No content source at all (and not an intentionally blank editable
  // editor): render a visible notice instead of a silent empty editor —
  // this is almost always the agent sending made-up props (e.g. `source`
  // instead of `contentUrl`), and a blank panel makes that undiagnosable.
  if (!contentUrl && inlineContent.trim() === '' && !editable) {
    const receivedKeys = Object.keys(props);
    return (
      <div data-testid="aui-markdown-editor" className={wrapperClass}>
        {title && (
          <h4 className="text-sm font-semibold text-foreground mb-2">{title}</h4>
        )}
        <div
          className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/50 p-3"
          data-testid="aui-markdown-editor-empty"
        >
          <p className="text-xs text-muted-foreground">
            No content — MarkdownEditor needs <code>content</code> or{' '}
            <code>contentUrl</code>.
            {receivedKeys.length > 0 && (
              <> Received props: <code>{receivedKeys.join(', ')}</code>.</>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="aui-markdown-editor" className={wrapperClass}>
      {title && (
        <h4 className="text-sm font-semibold text-foreground mb-2">{title}</h4>
      )}
      <MarkdownEditor
        key={previousContent ?? ''}
        value={editable ? editedContent : content}
        onChange={editable ? handleChange : () => {}}
        readOnly={!editable}
        minHeight={minHeight}
        maxHeight={maxHeight}
        fill={fill}
        suppressHtmlProcessing
      />
      {editable && actions && actions.length > 0 && (
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            isLoading={isSaving}
          >
            {actions[0].label ?? 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}
